import React, { useRef, useState, useEffect } from "react";
import { MoveLeft } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { Dropdown, Button, Spinner } from "react-bootstrap";
import CommonDropdown from "../common/common_dropDown";
import deleteIcon from "../../assets/svg/delete-po.svg";
import { submitTripCostAPI, updateTripCostAPI, getTripCostEditApi, getTripDetailApi, saveAttachmentApi } from "./trip-cost-api";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import { ReactComponent as DownArrowIc } from "../../assets/svg/down_arrow_ics.svg";
import totalAmtIc from "../../assets/svg/rupee.svg";
import fuelCostIc from "../../assets/svg/fuel-cost-ic.svg";
import fastagIc from "../../assets/svg/fastag-ic.svg";
import otherCostIc from "../../assets/svg/other-cost.svg";
import upload from "../../assets/svg/upload.svg";
import attachment from "../../assets/png/attachment.png";
import download from "../../assets/svg/download.svg";
import closeAttachment from "../../assets/svg/close-red.svg";
import "../common/skeleton-effect.css";
import "./trip-cost-create.css";
import CommonSnackbar from "../common/reusable-commponent";
import { sanitizeInput, sanitizeNumericInput, downloadBlobFile, validateFileType, validateFileSize } from "../common/security-utils";

export const TripCostCreate = () => {
  const apiUrl = process.env.REACT_APP_API_URL;

  const location = useLocation();
  const navigate = useNavigate();
  const { id } = location.state || {};

  useEffect(() => {
    if (id != null) {
      getTripsCostEdit(id);
    }
  }, [id]);

  const [snackbar, setSnackbar] = useState({
    show: false,
    message: "",
    status: null,
  });

  const [isLoader, setIsLoader] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [tripIDOption, setTripIDOption] = useState(null);
  const [tripDetails, setTripDetails] = useState({});
  const [selectedTab, setSelectedTab] = useState("Fuel Cost");
  const [stopsList, setStopsList] = useState([]);

  const [selectAll, setSelectAll] = useState(false);

  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    const updatedData = stopsList.map((row) => ({
      ...row,
      selected: newSelectAll,
    }));
    setSelectAll(newSelectAll);
    setStopsList(updatedData);
  };

  const handleRowSelect = (index) => {
    const updatedData = [...stopsList];
    updatedData[index].selected = !updatedData[index].selected;
    setStopsList(updatedData);
    setSelectAll(updatedData.every((row) => row.selected));
  };

  const [errorMsg, setErrorMsg] = useState({
    milestone_id: "",
    location_id: "",
    trip_id: "",
    vendor_id: "",
    stops: "",
  });

  const [fuelList, setFuelList] = useState([]);
  const [fastagList, setFastagList] = useState([]);
  const [otherCostList, setOtherCostList] = useState([]);

  const [groupedError, setGroupedError] = useState({});
  const [removedFuelList, setRemovedFuelList] = useState([]);
  const [removedFastagList, setRemovedFastagList] = useState([]);
  const [removedOtherCostList, setRemovedOtherCostList] = useState([]);

  const fileInputRef = useRef(null);
  const [image, setImage] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [attachmentURLList, setAttachmentURLList] = useState([]);
  const [attachmentList, setAttachmentList] = useState([]);
  const [removedAttachmentList, setRemovedAttachmentList] = useState([]);

  // const [activeBtn, setActiveBtn] = useState("details");
  const handleInputFile = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // VAPT: Validate file type and size
      if (!validateFileType(file, ["image/*", "application/pdf"])) {
        setSnackbar({
          show: true,
          message: "Invalid file type. Only images and PDFs are allowed.",
          status: false,
        });
        return;
      }

      if (!validateFileSize(file, 5)) {
        setSnackbar({
          show: true,
          message: "File size exceeds 5MB limit.",
          status: false,
        });
        return;
      }

      const reader = new FileReader();
      reader.onload = async () => {
        const base64String = await reader.result;
        setAttachmentList([
          ...attachmentList,
          {
            name: file.name,
            base64: base64String,
            size: formatBytes(file.size),
          },
        ]);
      };
      reader.readAsDataURL(file);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragging(false);

    const file = event.dataTransfer.files[0];
    if (file) {
      // VAPT: Validate file type and size
      if (!validateFileType(file, ["image/*", "application/pdf"])) {
        setSnackbar({
          show: true,
          message: "Invalid file type. Only images and PDFs are allowed.",
          status: false,
        });
        return;
      }

      if (!validateFileSize(file, 5)) {
        setSnackbar({
          show: true,
          message: "File size exceeds 5MB limit.",
          status: false,
        });
        return;
      }

      const reader = new FileReader();
      reader.onload = async () => {
        const base64String = await reader.result;
        setAttachmentList([
          ...attachmentList,
          {
            name: file.name,
            base64: base64String,
            size: formatBytes(file.size),
          },
        ]);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDelete = () => {
    setImage(null);
    fileInputRef.current.value = "";
  };

  const downloadFile = (fileUrl, fileName) => {
    downloadBlobFile(fileUrl, fileName);
  };

  const getErrorMsg = (index, field, category) => {
    if (groupedError[category]?.[index]?.[field]) {
      const errorMatched = groupedError[category][index][field];
      return Array.isArray(errorMatched) ? errorMatched[0] : errorMatched;
    }
    return "";
  };

  const submitForm = async () => {
    setIsLoader(true);
    const updatedFuelData = fuelList
      .filter((item) => item.location !== null)
      .map((item) => ({
        id: item.id ?? 0,
        location_id: item.location != null ? item.location.value : "",
        fuelbank: item.fuelbank ?? "",
        quantity: item.quantity ?? "",
        unit_price: item.unit_price ?? "",
        totalprice: item.totalprice ?? "",
      }));

    const updatedFastagData = fastagList
      .filter((item) => item.location !== null)
      .map((item) => ({
        id: item.id ?? 0,
        location_id: item.location != null ? item.location.value : "",
        amount: item.amount ?? "",
      }));

    try {
      const payloadData = {
        trip_id: tripIDOption != null ? tripIDOption.value : "",
        total_cost: 0,
        fuel_costs: updatedFuelData,
        fasttag_cost: updatedFastagData,
        other_cost: otherCostList.map(item => ({
          ...item,
          description: item.description ?? "",
          cost: item.cost ?? ""
        })),
        removed_attachments: removedAttachmentList,
        removed_fuels: removedFuelList,
        removed_fast_tags: removedFastagList,
        removed_other_costs: removedOtherCostList,
      };

      let response = null;
      if (id == null) {
        response = await submitTripCostAPI(payloadData);
      } else {
        response = await updateTripCostAPI(id, payloadData);
      }

      if (response?.data?.success === true) {
        setSnackbar({
          show: true,
          message: response.data.message || "Saved successfully",
          status: true,
        });

        if (attachmentList.length === 0) {
          navigate(-1);
        } else {
          saveAttachment(response.data.id);
        }
      } else {
        const validationTemp = {
          trip_id: "",
          fuel_costs: "",
          fasttag_cost: "",
          other_cost: "",
        };
        const groupedErrorTemp = {
          fuel_costs: {},
          fasttag_cost: {},
          other_cost: {},
        };

        if (response?.data?.error) {
          Object.entries(response.data.error).forEach(([key, value]) => {
            const match = key.match(/^(fuel_costs|fasttag_cost|other_cost)\.(\d+)\.(.+)$/);
            if (match) {
              const category = match[1];
              const index = match[2];
              const field = match[3];

              if (!groupedErrorTemp[category][index]) groupedErrorTemp[category][index] = {};
              groupedErrorTemp[category][index][field] = value;
            } else {
              validationTemp[key] = value.length === 0 ? "" : value[0];
            }
          });
        }
        setErrorMsg(validationTemp);
        setGroupedError(groupedErrorTemp);
        setSnackbar({
          show: true,
          message: response?.data?.message || "Validation error occurred",
          status: false,
        });
      }
    } catch (err) {
      setSnackbar({
        show: true,
        message: "An unexpected error occurred",
        status: false,
      });
    } finally {
      setIsLoader(false);
    }
  };

  const saveAttachment = async (parentId) => {
    try {
      const data = {
        attachments: attachmentList.map((item) => item.base64),
      };
      const response = await saveAttachmentApi(parentId, data);
      if (response?.data?.success === true) {
        navigate(-1);
      }
    } catch (err) {
      // Error handled by api-service
    }
  };

  const getTripsCostEdit = async (id) => {
    setIsLoadingData(true);
    try {
      const response = await getTripCostEditApi(id);

      if (response?.data?.success === true) {
        const tripDetail = response.data.trip_cost ?? {};
        setTripIDOption({
          value: tripDetail.trip_id,
          label: tripDetail.trip_code,
        });
        getTripDetails(tripDetail.trip_id);

        setFuelList(
          (tripDetail.fual_cost || [])?.map((item) => ({
            id: item.id,
            fuelbank: sanitizeInput(item.fuelbank),
            location: { label: item.location, value: item.location_id },
            quantity: sanitizeNumericInput(item.quentity),
            unit_price: sanitizeNumericInput(item.unit_price),
            totalprice: sanitizeNumericInput(item.totalprice),
          }))
        );
        setFastagList(
          (tripDetail.fast_tag_cost || [])?.map((item) => ({
            id: item.id,
            location: { label: item.location, value: item.location_id },
            amount: sanitizeNumericInput(item.amount),
          }))
        );
        setOtherCostList((tripDetail.other_cost ?? []).map(item => ({
          ...item,
          description: sanitizeInput(item.description),
          cost: sanitizeNumericInput(item.cost)
        })));
        setAttachmentURLList(tripDetail.attachments ?? []);
      }
    } catch (err) {
      // Error handled by api-service
    } finally {
      setIsLoadingData(false);
    }
  };

  const getTripDetails = async (id) => {
    try {
      const response = await getTripDetailApi(id);
      if (response?.data?.success === true) {
        const trip = response.data.Trip ?? {};
        setTripDetails(trip);

        // Prefill fuel cost rows from fuel entries logged for this trip's
        // vehicle, so previously recorded fuel fills aren't lost/re-entered
        // as zero. Only applies when creating fresh (no rows yet).
        if (fuelList.length === 0 && Array.isArray(trip.fuel_entries) && trip.fuel_entries.length > 0) {
          setFuelList(
            trip.fuel_entries.map((entry) => ({
              id: "",
              fuelbank: "",
              location: null,
              quantity: entry.quantity ?? "",
              unit_price: entry.unit_price ?? "",
              totalprice: entry.amount ?? "",
            }))
          );
        }
      }
    } catch (err) {
      // Error handled by api-service
    }
  };

  const [startDateSave, setStartDateSave] = useState(null);
  const [endDateSave, setEndDateSave] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const handleDateChange = ({ start, end }) => {
    setStartDate(start);
    setEndDate(end);

    const formattedStart = start ? format(start, "yyyy-MM-dd") : null;
    const formattedEnd = end ? format(end, "yyyy-MM-dd") : null;

    setStartDateSave(formattedStart);
    setEndDateSave(formattedEnd);
  };

  return (
    <div className="trip-cost-create-container">
      <div className="new-trip-cost-header">
        <div className="header-title-section">
          <MoveLeft
            className="cursor-pointer"
            onClick={() => {
              navigate(-1);
            }}
          />
          <p className="fw-semibold m-0">New trip cost</p>
        </div>
        <div className="header-actions-section">
          <Button
            className="cursor-pointer rounded-pill px-4 py-2 cancel-btn fs-15 fw-500"
            onClick={() => {
              navigate(-1);
            }}
          >
            Cancel
          </Button>
          <Button
            className={`cursor-pointer dark-purple-btn ${isLoader ? "disabled-btn" : ""} rounded-pill py-2 px-4 d-flex justify-content-center align-items-center save-btn`}
            onClick={() => {
              submitForm();
            }}
          >
            {isLoader ? <Spinner animation="border" size="sm" /> : id != null ? "Update" : "Save"}
          </Button>
        </div>
      </div>
      <div className="col-12 ">
        {isLoadingData ? (
          <>
            <div className="row mb-2 mx-0">
              <div className="col-12 col-md-6 pe-0">
                <div className="bg-white rounded-4 d-flex flex-column align-items-start px-2 py-2">
                  <div className="skeleton skeleton-text-lg mb-3" style={{ width: "140px" }} />
                  <div className="col-12">
                    <div className="row">
                      <div className="mt-2 col-12 col-lg-6 d-flex flex-column align-items-start gap-2">
                        <div className="skeleton skeleton-text-sm" style={{ width: "60px" }} />
                        <div className="skeleton" style={{ width: "100%", height: "40px" }} />
                      </div>
                      <div className="mt-2 col-12 col-lg-6  d-flex flex-column align-items-start gap-2 ps-0">
                        <div className="skeleton skeleton-text-sm" style={{ width: "70px" }} />
                        <div className="skeleton" style={{ width: "100%", height: "40px" }} />
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 col-12 d-flex flex-column align-items-start gap-2">
                    <div className="skeleton skeleton-text-sm" style={{ width: "60px" }} />
                    <div className="skeleton" style={{ width: "100%", height: "40px" }} />
                  </div>
                </div>
              </div>
              <div className="col-12 col-md-6 pe-0">
                <div className="bg-white rounded-4 d-flex flex-column align-items-start px-2 py-2">
                  <div className="skeleton skeleton-text-lg mb-3" style={{ width: "130px" }} />
                  <div className="col-12">
                    <div className="row">
                      <div className="mt-2 col-12 col-lg-6 d-flex flex-column align-items-start gap-2">
                        <div className="skeleton skeleton-text-sm" style={{ width: "40px" }} />
                        <div className="skeleton" style={{ width: "100%", height: "40px" }} />
                      </div>
                      <div className="mt-2 col-12 col-lg-6 d-flex flex-column align-items-start gap-2 ps-0">
                        <div className="skeleton skeleton-text-sm" style={{ width: "90px" }} />
                        <div className="skeleton" style={{ width: "100%", height: "40px" }} />
                      </div>
                    </div>
                    <div className="row">
                      <div className="mt-2 col-12 col-lg-6 d-flex flex-column align-items-start gap-2">
                        <div className="skeleton skeleton-text-sm" style={{ width: "90px" }} />
                        <div className="skeleton" style={{ width: "100%", height: "40px" }} />
                      </div>
                      <div className="mt-2 col-12 col-lg-6 d-flex flex-column align-items-start gap-2 ps-0">
                        <div className="skeleton skeleton-text-sm" style={{ width: "50px" }} />
                        <div className="skeleton" style={{ width: "100%", height: "40px" }} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="m-2 bg-white rounded-4 p-3">
              <div className="d-flex align-items-center justify-content-start gap-2 mb-3">
                {Array.from({ length: 4 }).map((_, idx) => (
                  <div key={idx} className="col-6 col-md-4 col-lg-2 rounded-3 p-2 d-flex align-items-center justify-content-start" style={{ background: "#F8F8F8" }}>
                    <div className="skeleton" style={{ width: "28px", height: "28px", borderRadius: "50%" }} />
                    <div className="ms-2 flex-grow-1">
                      <div className="skeleton skeleton-text-sm mb-2" style={{ width: "70px" }} />
                      <div className="skeleton skeleton-text" style={{ width: "60px" }} />
                    </div>
                  </div>
                ))}
              </div>

              <div className="d-flex gap-2 mb-3">
                {Array.from({ length: 3 }).map((_, idx) => (
                  <div key={idx} className="skeleton skeleton-tab" />
                ))}
              </div>

              <div className="mt-3">
                <div className="skeleton skeleton-table-header mb-2" />
                {Array.from({ length: 3 }).map((_, idx) => (
                  <div key={idx} className="skeleton skeleton-table-row" />
                ))}
              </div>

              <div className="mt-3">
                <div className="skeleton skeleton-button" />
              </div>
            </div>

            <div className="m-2 bg-white rounded-4 p-3">
              <div className="skeleton skeleton-text-lg mb-3" style={{ width: "120px" }} />
              <div className="skeleton skeleton-card" style={{ height: "150px" }} />
            </div>
          </>
        ) : (
          <>
            <div className="row mb-2">
              <div className="col-12 col-md-6">
                <div className="trip-details-card">
                  <div className="trip-details-title">
                    Trip cost details
                  </div>
                  <div className="col-12">
                    <div className="row">
                      <div className="mt-2 col-12 col-lg-6  d-flex flex-column align-items-start">
                        <div className="trip-details-label">
                          Trip ID<span className="text-danger">*</span>
                        </div>
                        <div className="w-100">
                          <CommonDropdown
                            apiUrl={`${apiUrl}trips/autosuggestion`}
                            placeholder="Select"
                            value={tripIDOption}
                            onSelect={(item) => {
                              setTripIDOption(item);
                              getTripDetails(item.value);
                            }}
                          />
                        </div>
                        <span className="error-message-span">
                          {errorMsg.trip_id}
                        </span>
                      </div>
                      <div className="mt-2 col-12 col-lg-6  d-flex flex-column align-items-start  ps-0">
                        <div className="trip-details-label">
                          Milestone
                        </div>
                        <div className="trip-details-value-box">
                          <span className="trip-details-value-text">
                            {tripDetails.milestone}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 col-12  d-flex flex-column align-items-start">
                    <div className="trip-details-label">
                      Location
                    </div>
                    <div className="trip-details-value-box">
                      <span className="trip-details-value-text">
                        {tripDetails.location}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-12 col-md-6 ">
                <div className="trip-details-card">
                  <div className="trip-details-title">
                    Vehicle details
                  </div>
                  <div className="col-12">
                    <div className="row">
                      <div className="mt-2 col-12 col-lg-6  d-flex flex-column align-items-start">
                        <div className="trip-details-label">
                          VIN
                        </div>
                        <div className="trip-details-value-box">
                          <span className="trip-details-value-text">
                            {tripDetails.vin}
                          </span>
                        </div>
                      </div>
                      <div className="mt-2 col-12 col-lg-6  d-flex flex-column align-items-start  ps-0">
                        <div className="trip-details-label">
                          License Plate
                        </div>
                        <div className="trip-details-value-box">
                          <span className="trip-details-value-text">
                            {tripDetails.license_plate}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="row">
                      <div className="mt-2 col-12 col-lg-6  d-flex flex-column align-items-start">
                        <div className="trip-details-label">
                          Vehicle Type
                        </div>
                        <div className="trip-details-value-box">
                          <span className="trip-details-value-text">
                            {tripDetails.vehicle_name}
                          </span>
                        </div>
                      </div>
                      <div className="mt-2 col-12 col-lg-6  d-flex flex-column align-items-start ps-0">
                        <div className="trip-details-label">
                          Vendor
                        </div>
                        <div className="trip-details-value-box">
                          <span className="trip-details-value-text">
                            {tripDetails.vendor_name}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="cost-summary-section">
              <div className="cost-summary-cards-wrapper">
                <div className="col-6 col-md-4 col-lg-2 cost-card cost-card-total">
                  <div className="cost-icon-box cost-icon-total">
                    <img src={totalAmtIc} alt="total cost" />
                  </div>
                  <div className="cost-info-wrapper">
                    <div className="cost-label-text">
                      Total Cost
                    </div>
                    <div className="cost-value-text">
                      {fuelList.reduce((sum, item) => sum + Number(item.totalprice), 0) + fastagList.reduce((sum, item) => sum + Number(item.amount), 0) + otherCostList.reduce((sum, item) => sum + Number(item.cost), 0)}
                    </div>
                  </div>
                </div>
                <div className="col-6 col-md-4 col-lg-2 cost-card cost-card-fuel">
                  <div className="cost-icon-box cost-icon-fuel">
                    <img src={fuelCostIc} alt="fuelCostIc" />
                  </div>
                  <div className="cost-info-wrapper">
                    <div className="cost-label-text">
                      Fuel Cost
                    </div>
                    <div className="cost-value-text">
                      {fuelList.reduce((sum, item) => sum + Number(item.totalprice), 0)}
                    </div>
                  </div>
                </div>

                <div className="col-6 col-md-4 col-lg-2 cost-card cost-card-fastag">
                  <div className="cost-icon-box cost-icon-fastag">
                    <img src={fastagIc} alt="fastagIc" />
                  </div>
                  <div className="cost-info-wrapper">
                    <div className="cost-label-text">
                      Fastag Cost
                    </div>
                    <div className="cost-value-text">
                      {fastagList.reduce((sum, item) => sum + Number(item.amount), 0)}
                    </div>
                  </div>
                </div>

                <div className="col-6 col-md-4 col-lg-2 cost-card cost-card-other">
                  <div className="cost-icon-box cost-icon-other">
                    <img src={otherCostIc} alt="otherCostIc" />
                  </div>
                  <div className="cost-info-wrapper">
                    <div className="cost-label-text">
                      Other Cost
                    </div>
                    <div className="cost-value-text">
                      {otherCostList.reduce((sum, item) => sum + Number(item.cost), 0)}
                    </div>
                  </div>
                </div>
              </div>
              <div className="row mt-2">
                <div className="col-12 ">
                  <div className="row">
                    <div className="tab-nav-wrapper">
                      <div
                        className={`tab-button ${selectedTab === "Fuel Cost" ? "active" : "inactive"}`}
                        onClick={() => {
                          setSelectedTab("Fuel Cost");
                        }}
                      >
                        Fuel Cost
                      </div>
                      <div
                        className={`tab-button ${selectedTab === "Fastag Cost" ? "active" : "inactive"}`}
                        onClick={() => {
                          setSelectedTab("Fastag Cost");
                        }}
                      >
                        Fastag Cost
                      </div>

                      <div
                        className={`tab-button ${selectedTab === "Other Cost" ? "active" : "inactive"}`}
                        onClick={() => {
                          setSelectedTab("Other Cost");
                        }}
                      >
                        Other Cost
                      </div>
                    </div>

                    {selectedTab === "Fuel Cost" && (
                      <>
                        <div className="cost-table-container">
                          <div className="cost-table-wrapper ">
                            <table className="cost-table-root" border="1" cellPadding="10">
                              <thead className="cost-table-thead">
                                <tr>
                                  <th className="ps-2">FUEL STATION</th>
                                  <th className="ps-2">LOCATION</th>
                                  <th className="ps-2">QUANTITY</th>
                                  <th className="ps-2">PRICE PER LITER</th>
                                  <th className="ps-2">TOTAL PRICE</th>
                                  <th className="ps-2"></th>
                                </tr>
                              </thead>
                              <tbody>
                                {fuelList.map((row, index1) => (
                                  <tr key={row.id || `new-fuel-${index1}`} className="cost-table-row">
                                    <td className="cost-table-cell">
                                      <input
                                        type="text"
                                        className="form-control rounded-3 vendor-form-input"
                                        id={`mobile` + index1}
                                        placeholder="Enter here"
                                        value={row.fuelbank}
                                        onChange={(e) => {
                                          const updatedFuelList = [...fuelList];
                                          updatedFuelList[index1] = {
                                            ...updatedFuelList[index1],
                                            fuelbank: e.target.value,
                                          };
                                          setFuelList(updatedFuelList);
                                        }}
                                        style={{
                                          borderColor: getErrorMsg(index1, "fuelbank", "fuel_costs") !== "" ? "red" : "#EBEBEB",
                                        }}
                                      />{" "}
                                      <span className="fs-12 fw-400 cardTextRed">{getErrorMsg(index1, "fuelbank", "fuel_costs")}</span>
                                    </td>
                                    <td className="cost-table-cell">
                                      <div className="dropdown-cell-wrapper">
                                        <CommonDropdown
                                          apiUrl={`${apiUrl}get_locations`}
                                          placeholder="Select"
                                          value={row.location}
                                          onSelect={(item) => {
                                            const updatedFuelList = [...fuelList];
                                            updatedFuelList[index1] = {
                                              ...updatedFuelList[index1],
                                              location: item,
                                            };
                                            setFuelList(updatedFuelList);
                                          }}
                                        />
                                        <span className="fs-12 fw-400 cardTextRed">{getErrorMsg(index1, "location_id", selectedTab === "Fuel Cost" ? "fuel_costs" : "fasttag_cost")}</span>
                                      </div>
                                    </td>

                                    <td className="cost-table-cell">
                                      <input
                                        type="text"
                                        className="form-control rounded-3 vendor-form-input"
                                        id={`quantity` + index1}
                                        placeholder="Enter here"
                                        value={row.quantity}
                                        onChange={(e) => {
                                          const tempDelivery = [...fuelList];
                                          tempDelivery[index1]["quantity"] = e.target.value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");
                                          tempDelivery[index1]["totalprice"] = (Number(e.target.value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1") ?? 0) * Number(tempDelivery[index1]["unit_price"] ?? 0)).toFixed(2);
                                          setFuelList(tempDelivery);
                                        }}
                                        style={{
                                          borderColor: getErrorMsg(index1, "quantity", "fuel_costs") !== "" ? "red" : "#EBEBEB",
                                        }}
                                      />{" "}
                                      <span className="fs-12 fw-400 cardTextRed">{getErrorMsg(index1, "quantity", "fuel_costs")}</span>
                                    </td>

                                    <td className="cost-table-cell">
                                      <input
                                        type="text"
                                        className="form-control rounded-3 vendor-form-input"
                                        id={`unit_price` + index1}
                                        placeholder="Enter here"
                                        value={row.unit_price}
                                        onChange={(e) => {
                                          const tempDelivery = [...fuelList];
                                          tempDelivery[index1]["unit_price"] = e.target.value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");
                                          tempDelivery[index1]["totalprice"] = (parseFloat(tempDelivery[index1]["quantity"] ?? 0) * parseFloat(e.target.value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1") ?? 0)).toFixed(2);
                                          setFuelList(tempDelivery);
                                        }}
                                        style={{
                                          borderColor: getErrorMsg(index1, "unit_price", "fuel_costs") !== "" ? "red" : "#EBEBEB",
                                        }}
                                      />{" "}
                                      <span className="fs-12 fw-400 cardTextRed">{getErrorMsg(index1, "unit_price", "fuel_costs")}</span>
                                    </td>
                                    <td className="cost-table-cell">
                                      <input type="text" className="form-control rounded-3 vendor-form-input" id={`totalprice` + index1} value={row.totalprice} readOnly={true} />
                                    </td>

                                    <td>
                                      <div
                                        className="mx-2 ps-2 d-flex align-items-center justify-content-center delete-action-wrapper"
                                        onClick={() => {
                                          if (row.id !== null && row.id !== "") {
                                            setRemovedFuelList([...removedFuelList, row.id]);
                                          }
                                          const tempFuelList = [...fuelList];
                                          tempFuelList.splice(index1, 1);
                                          setFuelList(tempFuelList);
                                        }}
                                      >
                                        <img src={deleteIcon} alt="delete" />
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                        <div className="col-xl-12 mt-2">
                          <Button
                            className={`cursor-pointer border-0 rounded-pill py-2 px-4 d-flex justify-content-center align-items-center add-cost-btn`}
                            onClick={() => {
                              setFuelList([
                                ...fuelList,
                                {
                                  id: "",
                                  fuelbank: "",
                                  location: null,
                                  quantity: "",
                                  unit_price: "",
                                  totalprice: "",
                                },
                              ]);
                            }}
                          >
                            Add fuel cost
                          </Button>
                        </div>
                      </>
                    )}

                    {selectedTab === "Fastag Cost" && (
                      <>
                        <div className="cost-table-container">
                          <div className="cost-table-wrapper ">
                            <table className="cost-table-root" border="1" cellPadding="10">
                              <thead className="cost-table-thead">
                                <tr>
                                  <th className="ps-2">LOCATION</th>
                                  <th className="ps-2">AMOUNT</th>
                                  <th className="ps-2"></th>
                                </tr>
                              </thead>
                              <tbody>
                                {fastagList.map((row, index1) => (
                                  <tr key={row.id || `new-fastag-${index1}`} className="cost-table-row">
                                    <td className="cost-table-cell">
                                      <div className="dropdown-cell-wrapper">
                                        <CommonDropdown
                                          apiUrl={`${apiUrl}get_locations`}
                                          placeholder="Select"
                                          value={row.location}
                                          onSelect={(item) => {
                                            const updatedFastagList = [...fastagList];
                                            updatedFastagList[index1] = {
                                              ...updatedFastagList[index1],
                                              location: item,
                                            };
                                            setFastagList(updatedFastagList);
                                          }}
                                        />
                                        <span className="fs-12 fw-400 cardTextRed">{getErrorMsg(index1, "location_id", selectedTab === "Fuel Cost" ? "fuel_costs" : "fasttag_cost")}</span>
                                      </div>
                                    </td>

                                    <td className="cost-table-cell">
                                      <input
                                        type="text"
                                        className="form-control rounded-3 vendor-form-input"
                                        id={`amount` + index1}
                                        placeholder="Enter here"
                                        value={row.amount}
                                        onChange={(e) => {
                                          const tempFastag = [...fastagList];
                                          tempFastag[index1]["amount"] = e.target.value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");

                                          setFastagList(tempFastag);
                                        }}
                                        style={{
                                          borderColor: getErrorMsg(index1, "amount", "fasttag_cost") !== "" ? "red" : "#EBEBEB",
                                        }}
                                      />{" "}
                                      <span className="fs-12 fw-400 cardTextRed">{getErrorMsg(index1, "amount", "fasttag_cost")}</span>
                                    </td>

                                    <td>
                                      <div
                                        className="mx-2 ps-2 d-flex align-items-center justify-content-center delete-action-wrapper"
                                        onClick={() => {
                                          if (row.id !== null && row.id !== "") {
                                            setRemovedFastagList([...removedFastagList, row.id]);
                                          }
                                          const tempFastagList = [...fastagList];
                                          tempFastagList.splice(index1, 1);
                                          setFastagList(tempFastagList);
                                        }}
                                      >
                                        <img src={deleteIcon} alt="delete" />
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                        <div className="col-xl-12 mt-2">
                          <Button
                            className={`cursor-pointer border-0 rounded-pill py-2 px-4 d-flex justify-content-center align-items-center add-cost-btn`}
                            onClick={() => {
                              setFastagList([
                                ...fastagList,
                                {
                                  id: "",
                                  location: null,
                                  amount: "",
                                },
                              ]);
                            }}
                          >
                            Add fastag cost
                          </Button>
                        </div>
                      </>
                    )}

                    {selectedTab === "Other Cost" && (
                      <>
                        <div className="cost-table-container">
                          <div className="cost-table-wrapper ">
                            <table className="cost-table-root" border="1" cellPadding="10">
                              <thead className="cost-table-thead">
                                <tr>
                                  <th className="ps-2">DESCRIPTION</th>
                                  <th className="ps-2">AMOUNT</th>
                                  <th className="ps-2"></th>
                                </tr>
                              </thead>
                              <tbody>
                                {otherCostList.map((row, index1) => (
                                  <tr key={row.id} className="cost-table-row">
                                    <td className="cost-table-cell">
                                      <input
                                        type="text"
                                        className="form-control rounded-3 vendor-form-input"
                                        id={`description` + index1}
                                        placeholder="Enter here"
                                        value={row.description}
                                        onChange={(e) => {
                                          const tempOtherCostList = [...otherCostList];
                                          tempOtherCostList[index1]["description"] = e.target.value;
                                          setOtherCostList(tempOtherCostList);
                                        }}
                                        style={{
                                          borderColor: getErrorMsg(index1, "description", "other_cost") !== "" ? "red" : "#EBEBEB",
                                        }}
                                      />{" "}
                                      <span className="fs-12 fw-400 cardTextRed">{getErrorMsg(index1, "description", "other_cost")}</span>
                                    </td>

                                    <td className="cost-table-cell">
                                      <input
                                        type="text"
                                        className="form-control rounded-3 vendor-form-input"
                                        id={`cost` + index1}
                                        placeholder="Enter here"
                                        value={row.cost}
                                        onChange={(e) => {
                                          const tempOtherCostList = [...otherCostList];
                                          tempOtherCostList[index1]["cost"] = e.target.value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");

                                          setOtherCostList(tempOtherCostList);
                                        }}
                                        style={{
                                          borderColor: getErrorMsg(index1, "cost", "other_cost") !== "" ? "red" : "#EBEBEB",
                                        }}
                                      />{" "}
                                      <span className="fs-12 fw-400 cardTextRed">{getErrorMsg(index1, "cost", "other_cost")}</span>
                                    </td>

                                    <td>
                                      <div
                                        className="mx-2 ps-2 d-flex align-items-center justify-content-center delete-action-wrapper"
                                        onClick={() => {
                                          if (row.id !== null && row.id !== "") {
                                            setRemovedOtherCostList([...removedOtherCostList, row.id]);
                                          }
                                          const tempOtherCostList = [...otherCostList];
                                          tempOtherCostList.splice(index1, 1);
                                          setOtherCostList(tempOtherCostList);
                                        }}
                                      >
                                        <img src={deleteIcon} alt="delete" />
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                        <div className="col-xl-12 mt-2">
                          <Button
                            className={`cursor-pointer border-0 rounded-pill py-2 px-4 d-flex justify-content-center align-items-center add-cost-btn`}
                            onClick={() => {
                              setOtherCostList([
                                ...otherCostList,
                                {
                                  id: "",
                                  description: "",
                                  cost: "",
                                },
                              ]);
                            }}
                          >
                            Add other cost
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="attachments-section">
              <div className="row">
                <div className="col-xl-12">
                  <p className="attachments-title">
                    Attachments{" "}
                  </p>
                </div>
                <div className="row pt-0">
                  {attachmentURLList.length > 0 && (
                    <div className="attachment-list-wrapper">
                      {attachmentURLList.map((item, attachIndex) => (
                        <div key={attachIndex} className="attachment-item-card">
                          <img src={attachment} className="upload-icon-style" style={{ width: "37px", height: "36px" }} />
                          <div className="attachment-details">
                            <div className="attachment-name-text">
                              {item.name}
                            </div>
                            <p className="attachment-size-text">
                              {item.size}
                            </p>
                          </div>

                          {item.url && (
                            <div
                              onClick={() => {
                                downloadFile(item.url, item.url.split("/").pop());
                              }}
                              className="attachment-action-btn"
                            >
                              <img src={download} style={{ width: "12.8px", height: "12px" }} />
                            </div>
                          )}

                          <div className="attachment-action-btn close-attachment-btn">
                            <img
                              src={closeAttachment}
                              style={{ width: "12.8px", height: "12px" }}
                              alt="remove"
                              onClick={() => {
                                setRemovedAttachmentList([...removedAttachmentList, item.id]);
                                setAttachmentURLList(attachmentURLList.filter((_, i) => i !== attachIndex));
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {attachmentList.length > 0 && (
                    <div className="attachment-list-wrapper">
                      {attachmentList.map((item, attachIndex) => (
                        <div key={attachIndex} className="attachment-item-card">
                          <img src={attachment} style={{ width: "37px", height: "36px" }} />
                          <div className="attachment-details-new">
                            <div className="attachment-name-text">
                              {item.name}
                            </div>
                            <p className="attachment-size-text">
                              {item.size}
                            </p>
                          </div>

                          <div className="attachment-action-btn close-attachment-btn">
                            <img
                              src={closeAttachment}
                              style={{ width: "12.8px", height: "12px" }}
                              onClick={() => {
                                setAttachmentList(attachmentList.filter((_, i) => i !== attachIndex));
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="col-12 p-2 d-flex flex-column rounded-4 align-items-center justify-content-center upload-document-zone">
                    <div onClick={handleInputFile} onDrop={handleDrop} onDragOver={(e) => e.preventDefault()} onDragEnter={() => setDragging(true)} onDragLeave={() => setDragging(false)} className="d-flex flex-column justify-content-center align-items-center gap-3">
                      {image ? (
                        <img
                          src={image}
                          alt="Uploaded Vehicle"
                          className="rounded"
                          style={{
                            width: "60px",
                            height: "60px",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <img src={upload} alt="Upload Icon" className="upload-icon-style" />
                      )}
                      <p className="fw-semibold m-0 p-0">Upload document</p>
                      <p className="m-0 fs-14 text-muted-grey p-0">Add a document here by dragging & dropping, or choose files manually.</p>
                    </div>

                    {image ? (
                      <Button className="cursor-pointer mt-1 bg-danger border-0 text-white rounded-pill px-4 py-2 fw-500" onClick={handleDelete}>
                        Delete
                      </Button>
                    ) : (
                      <Button className="cursor-pointer mt-1 border-0 rounded-pill text-white px-4 py-2 fw-500 select-file-btn" onClick={handleInputFile}>
                        Select a file
                      </Button>
                    )}

                    <input type="file" accept="image/*" ref={fileInputRef} className="d-none" onChange={handleFileChange} />
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      <CommonSnackbar
        show={snackbar.show}
        message={snackbar.message}
        isSuccess={snackbar.status}
        onClose={() => setSnackbar({ ...snackbar, show: false })}
      />
    </div>
  );
};
